import { useState } from 'react';
import { useCharacterStore } from '@/state/characterStore';
import { useUIStore } from '@/state/uiStore';
import { Character } from '@/network/RestApi';
import { ui } from './theme';
import { CloseButton } from './CloseButton';

type StatKey = 'str' | 'agi' | 'vit' | 'ene';

const STAT_LABELS: { key: StatKey; label: string; color: string }[] = [
  { key: 'str', label: 'STR', color: '#ff6b6b' },
  { key: 'agi', label: 'AGI', color: '#51cf66' },
  { key: 'vit', label: 'VIT', color: '#ffd166' },
  { key: 'ene', label: 'ENE', color: '#42a5f5' },
];

// PRD_SkillTree stat priority per class: weights sum to 1.0
const CLASS_WEIGHTS: Record<string, Record<StatKey, number>> = {
  warrior: { str: 0.5, vit: 0.3, agi: 0.15, ene: 0.05 },
  wizard:  { ene: 0.5, agi: 0.3, str: 0.1,  vit: 0.1  },
  archer:  { agi: 0.5, str: 0.3, ene: 0.1,  vit: 0.1  },
};

function buildSuggestion(klass: string, points: number): Record<StatKey, number> {
  const weights = CLASS_WEIGHTS[klass] ?? CLASS_WEIGHTS.warrior;
  const result: Record<StatKey, number> = { str: 0, agi: 0, vit: 0, ene: 0 };
  let remaining = points;
  // Allocate by weight, floor each, then give leftover to highest-weight stat
  const entries = (Object.entries(weights) as [StatKey, number][]).sort((a, b) => b[1] - a[1]);
  for (const [key, w] of entries) {
    result[key] = Math.floor(w * points);
    remaining -= result[key];
  }
  // Distribute leftover to highest-weight stats in order
  for (const [key] of entries) {
    if (remaining <= 0) break;
    result[key]++;
    remaining--;
  }
  return result;
}

export function StatPanel() {
  const ch = useCharacterStore((s) => s.current);
  const str = useCharacterStore((s) => s.str);
  const agi = useCharacterStore((s) => s.agi);
  const vit = useCharacterStore((s) => s.vit);
  const ene = useCharacterStore((s) => s.ene);
  const statPoints = useCharacterStore((s) => s.statPoints);
  const skillPoints = useCharacterStore((s) => s.skillPoints);
  const setStats = useCharacterStore((s) => s.setStats);
  const showToast = useUIStore((s) => s.showToast);
  const close = () => useUIStore.getState().set('stat', false);

  const [pending, setPending] = useState(false);
  // suggestion: null = not shown, Record = pending confirmation
  const [suggestion, setSuggestion] = useState<Record<StatKey, number> | null>(null);

  const stats: Record<StatKey, number> = { str, agi, vit, ene };

  if (!ch) return null;

  const allocate = async (stat: StatKey, amount: number) => {
    if (statPoints < amount || pending) return;
    setPending(true);
    try {
      const r = await Character.allocateStat(ch.id, { stat, amount });
      setStats({ str: r.str, agi: r.agi, vit: r.vit, ene: r.ene, statPoints: r.statPoints });
      showToast(`${stat.toUpperCase()} +${amount}`);
    } catch (e) {
      showToast((e as Error).message, 'error');
    } finally {
      setPending(false);
    }
  };

  const suggest = () => {
    if (statPoints < 1) return;
    setSuggestion(buildSuggestion(ch.class, statPoints));
  };

  const confirmSuggestion = async () => {
    if (!suggestion || pending) return;
    setPending(true);
    setSuggestion(null);
    try {
      // Apply each stat sequentially; skip zeros
      let last: { str: number; agi: number; vit: number; ene: number; statPoints: number } | null = null;
      for (const key of Object.keys(suggestion) as StatKey[]) {
        const amt = suggestion[key];
        if (amt <= 0) continue;
        last = await Character.allocateStat(ch.id, { stat: key, amount: amt });
      }
      if (last) {
        setStats({ str: last.str, agi: last.agi, vit: last.vit, ene: last.ene, statPoints: last.statPoints });
        showToast('Đã phân phối điểm tiềm năng');
      }
    } catch (e) {
      showToast((e as Error).message, 'error');
    } finally {
      setPending(false);
    }
  };

  const smallBtn = { ...ui.secondaryBtn, width: 44, height: 28, fontSize: 12, padding: 0 };
  const wideBtn = { ...ui.primaryBtn, width: 'auto', flex: 1, height: 36, fontSize: 13 };
  const cancelBtn = { ...ui.secondaryBtn, width: 'auto', flex: 1, height: 36, fontSize: 13 };

  return (
    <div style={{ position: 'absolute', top: 70, right: 10, width: 360, zIndex: 100, ...ui.panel }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <strong style={{ fontSize: 16 }}>Nhân vật</strong>
        <CloseButton onClick={close} />
      </div>

      <div style={{ fontSize: 12, color: '#9ec5fe', marginBottom: 4 }}>
        {ch.name} · Lv {ch.level} · Reset {ch.resetCount}
      </div>
      <div style={{ fontSize: 12, color: '#ffd166', marginBottom: 10 }}>
        Điểm tiềm năng: <strong>{statPoints}</strong> &nbsp;|&nbsp; Skill Points: <strong>{skillPoints}</strong>
      </div>

      {STAT_LABELS.map(({ key, label, color }) => {
        const delta = suggestion?.[key] ?? 0;
        return (
          <div
            key={key}
            style={{
              display: 'grid',
              gridTemplateColumns: '70px 1fr auto',
              alignItems: 'center',
              gap: 8,
              padding: '6px 0',
              borderBottom: '1px solid #1f2933',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
              <span style={{ color, fontWeight: 700, fontSize: 14 }}>{label}</span>
              <span style={{ color: '#f3e7c2', fontWeight: 600, fontSize: 14 }}>{stats[key]}</span>
              {delta > 0 && <span style={{ color: '#51cf66', fontSize: 11 }}>+{delta}</span>}
            </span>
            <span />
            <span style={{ display: 'flex', gap: 4 }}>
              <button style={smallBtn} disabled={statPoints < 1 || pending || !!suggestion} onClick={() => allocate(key, 1)}>+1</button>
              <button style={smallBtn} disabled={statPoints < 5 || pending || !!suggestion} onClick={() => allocate(key, 5)}>+5</button>
              <button style={smallBtn} disabled={statPoints < 10 || pending || !!suggestion} onClick={() => allocate(key, 10)}>+10</button>
            </span>
          </div>
        );
      })}

      <div style={{ marginTop: 12, display: 'flex', gap: 6 }}>
        {!suggestion ? (
          <button
            style={wideBtn}
            disabled={statPoints < 1 || pending}
            onClick={suggest}
          >
            Gợi ý ({ch.class})
          </button>
        ) : (
          <>
            <button style={wideBtn} disabled={pending} onClick={confirmSuggestion}>
              Xác nhận
            </button>
            <button style={cancelBtn} disabled={pending} onClick={() => setSuggestion(null)}>
              Huỷ
            </button>
          </>
        )}
      </div>

      {suggestion && (
        <div style={{ marginTop: 8, fontSize: 11, color: '#9ec5fe', background: 'rgba(76,209,255,0.07)', borderRadius: 4, padding: '6px 8px' }}>
          Gợi ý theo ưu tiên <strong style={{ color: '#4cd1ff' }}>{ch.class}</strong>:{' '}
          {STAT_LABELS.filter((s) => (suggestion[s.key] ?? 0) > 0)
            .map((s) => `${s.label} +${suggestion[s.key]}`)
            .join(', ')}
        </div>
      )}
    </div>
  );
}

